"use client"

import React, { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Badge } from "./badge"
import { Button } from "./button"
import { AlertTriangle, XCircle, Server, Database, Activity, Clock, RefreshCw, TrendingUp, ChevronLeft, ChevronRight } from "lucide-react"
import apiService from '../../services/apiService'

interface CriticalAlert {
  id: number
  type: string
  title: string
  description: string
  severity: "critical" | "high" | "medium" | "low"
  site_id: string
  client_id?: string
  timestamp: string
  count?: number
  service_name?: string
  status: "active" | "acknowledged" | "resolved" | "archived"
  uuid?: string
  batch_index?: number
  error_message?: string
  alert_type?: string
  rule_name?: string
  metadata?: any
}

export interface CriticalAlertsRef {
  addAlertFromPayload: (payload: PushPayload) => void
  refreshAlerts: () => Promise<void>
}

interface CriticalAlertsProps {
  filters?: {
    failureType: string
    severity: string
    status: string
    startTime: string
    endTime: string
  }
  sourceType?: string
  onStatusFilterChange?: (status: string) => void
}

interface PushPayload {
  id: number
  type: string
  alert_type?: string
  title: string
  description: string
  severity: "critical" | "high" | "medium" | "low"
  site_id: string
  client_id?: string
  timestamp?: string
  first_occurred?: string
  count?: number
  service_name?: string
  status: "active" | "acknowledged" | "resolved" | "archived"
  uuid?: string
  related_uuid?: string
  batch_index?: number
  related_batch_index?: number
  error_message?: string
  rule_name?: string
  metadata?: any
}

const transformPushPayloadToAlert = (payload: PushPayload): CriticalAlert => {
  return {
    id: payload.id,
    type: payload.alert_type || payload.type,
    title: payload.title,
    description: payload.description,
    severity: payload.severity,
    site_id: payload.site_id,
    client_id: payload.client_id,
    timestamp: payload.first_occurred || payload.timestamp || new Date().toISOString(),
    count: payload.count,
    service_name: payload.service_name,
    status: payload.status,
    uuid: payload.related_uuid || payload.uuid,
    batch_index: payload.related_batch_index || payload.batch_index,
    error_message: payload.error_message,
    alert_type: payload.alert_type,
    rule_name: payload.metadata?.rule_name || payload.rule_name,
    metadata: payload.metadata,
  }
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

function calculateWeekTrend(trendData: Record<string, number>): number {
  const dates = Object.keys(trendData).sort().reverse() // newest first
  const thisWeek = dates.slice(0, 7).reduce((sum, d) => sum + (trendData[d] || 0), 0)
  const lastWeek = dates.slice(7, 14).reduce((sum, d) => sum + (trendData[d] || 0), 0)
  if (lastWeek === 0) return thisWeek > 0 ? 100 : 0
  return Math.round(((thisWeek - lastWeek) / lastWeek) * 100)
}

export const CriticalAlerts = React.forwardRef<CriticalAlertsRef, CriticalAlertsProps>(({ filters, sourceType, onStatusFilterChange }, ref) => {
  const [alerts, setAlerts] = useState<CriticalAlert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filteredAlerts, setFilteredAlerts] = useState<CriticalAlert[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [weekTrend, setWeekTrend] = useState<number | null>(null)
  const [summaryStats, setSummaryStats] = useState<{
    active: number
    acknowledged: number
    resolvedToday: number
  } | null>(null)

  const totalPages = Math.ceil(totalCount / pageSize)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [filters, sourceType])

  const loadTrend = useCallback(async () => {
    try {
      const response = await apiService.getCriticalAlertsSummary({
        source_type: sourceType || undefined,
        failure_type: filters?.failureType !== 'all' ? filters?.failureType : undefined,
        severity: filters?.severity !== 'all' ? filters?.severity : undefined,
        start_time: filters?.startTime || undefined,
        end_time: filters?.endTime || undefined,
      })
      if (response.success) {
        const data = response.data as any
        const trend: Record<string, number> = data.recent_trend || {}
        setWeekTrend(calculateWeekTrend(trend))
        setSummaryStats({
          active: data.active_alerts ?? 0,
          acknowledged: data.acknowledged_alerts ?? 0,
          resolvedToday: data.resolved_today ?? 0,
        })
      }
    } catch {
      setWeekTrend(null)
      setSummaryStats(null)
    }
  }, [sourceType, filters?.failureType, filters?.severity, filters?.startTime, filters?.endTime])

  useEffect(() => {
    loadTrend()
  }, [loadTrend])

  const loadAlerts = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const apiFilters = {
        ...(filters ? {
          failure_type: filters.failureType !== "all" ? filters.failureType : undefined,
          severity: filters.severity !== "all" ? filters.severity : undefined,
          status: filters.status !== "all" ? filters.status : undefined,
          start_time: filters.startTime || undefined,
          end_time: filters.endTime || undefined,
        } : {}),
        source_type: sourceType || undefined,
        page,
        page_size: pageSize,
      }

      const response = await apiService.getCriticalAlerts(apiFilters)

      if (response.success) {
        const paginatedData = response.data as any
        const alertsData = paginatedData.results || response.data
        setTotalCount(paginatedData.count ?? alertsData.length)

        const transformedAlerts: CriticalAlert[] = alertsData.map((alert: any) => ({
          id: alert.id,
          type: alert.alert_type || alert.type,
          title: alert.title,
          description: alert.description,
          severity: alert.severity,
          site_id: alert.site_id,
          client_id: alert.client_id,
          timestamp: alert.first_occurred || alert.timestamp,
          count: alert.count,
          service_name: alert.service_name,
          status: alert.status,
          uuid: alert.related_uuid || alert.uuid,
          batch_index: alert.related_batch_index || alert.batch_index,
          error_message: alert.error_message,
          alert_type: alert.alert_type,
          rule_name: alert.metadata?.rule_name || alert.rule_name,
          metadata: alert.metadata,
        }))

        setAlerts(transformedAlerts)
        setFilteredAlerts(transformedAlerts)
      } else {
        setError(response.error || 'Failed to load alerts')
        setAlerts([])
        setFilteredAlerts([])
        setTotalCount(0)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      setAlerts([])
      setFilteredAlerts([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [filters, sourceType, page, pageSize])

  useEffect(() => {
    loadAlerts()
  }, [loadAlerts])

  // Function to add new alert from push payload
  const addAlertFromPayload = useCallback((payload: PushPayload) => {
    const newAlert = transformPushPayloadToAlert(payload)
    setAlerts(prevAlerts => [newAlert, ...prevAlerts])
    setFilteredAlerts(prevAlerts => [newAlert, ...prevAlerts])
  }, [])

  React.useImperativeHandle(ref, () => ({
    addAlertFromPayload,
    refreshAlerts: loadAlerts
  }))

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "service_unreachable":
        return <Server className="h-5 w-5" style={{ color: "#ef4444" }} />
      case "qure_services_unreachable":
        return <XCircle className="h-5 w-5" style={{ color: "#ef4444" }} />
      case "high_unprocessed_images":
        return <Database className="h-5 w-5" style={{ color: "#ff8697" }} />
      case "pipeline_failure":
        return <Activity className="h-5 w-5" style={{ color: "#ef4444" }} />
      // Custom alert types
      case "failed_images_detected":
        return <XCircle className="h-5 w-5" style={{ color: "#ef4444" }} />
      case "high_sync_pending":
        return <Clock className="h-5 w-5" style={{ color: "#ff8697" }} />
      case "high_cpu_usage":
        return <Activity className="h-5 w-5" style={{ color: "#ef4444" }} />
      case "lot_of_pending_images":
        return <Clock className="h-5 w-5" style={{ color: "#ff8697" }} />
      default:
        return <AlertTriangle className="h-5 w-5" style={{ color: "#ef4444" }} />
    }
  }

  const getSeverityBadge = (severity: string) => {
    const styles = {
      critical: {
        bg: "linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)",
        color: "#ef4444",
        border: "#ef4444",
      },
      high: {
        bg: "linear-gradient(135deg, rgba(255, 134, 151, 0.2) 0%, rgba(255, 134, 151, 0.2) 100%)",
        color: "#ff8697",
        border: "#ff8697",
      },
      medium: {
        bg: "linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(202, 138, 4, 0.2) 100%)",
        color: "#eab308",
        border: "#eab308",
      },
      low: {
        bg: "linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.2) 100%)",
        color: "#22c55e",
        border: "#22c55e",
      },
    }
    const style = styles[severity as keyof typeof styles]
    return (
      <Badge
        style={{
          background: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          fontSize: "14px",
          fontWeight: "600",
        }}
      >
        {severity.toUpperCase()}
      </Badge>
    )
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      active: {
        bg: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)",
        color: "#ef4444",
      },
      acknowledged: {
        bg: "linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(202, 138, 4, 0.15) 100%)",
        color: "#eab308",
      },
      resolved: {
        bg: "linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.15) 100%)",
        color: "#22c55e",
      },
      archived: {
        bg: "linear-gradient(135deg, rgba(148, 163, 184, 0.15) 0%, rgba(100, 116, 139, 0.15) 100%)",
        color: "#94a3b8",
      },
    }
    const style = styles[status as keyof typeof styles] ?? styles.resolved
    return (
      <Badge
        style={{
          background: style.bg,
          color: style.color,
          fontSize: "14px",
          fontWeight: "500",
        }}
      >
        {status.toUpperCase()}
      </Badge>
    )
  }

  const acknowledgeAlert = async (id: number) => {
    try {
      const response = await apiService.acknowledgeAlert(id.toString())
      if (response.success) {
        setAlerts(alerts.map((alert) => (alert.id === id ? { ...alert, status: "acknowledged" as const } : alert)))
        setFilteredAlerts(filteredAlerts.map((alert) => (alert.id === id ? { ...alert, status: "acknowledged" as const } : alert)))
        loadTrend()
      } else {
        console.error('Failed to acknowledge alert:', response.error)
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error)
    }
  }

  const resolveAlert = async (id: number) => {
    try {
      const response = await apiService.resolveAlert(id.toString())
      if (response.success) {
        setAlerts(alerts.map((alert) => (alert.id === id ? { ...alert, status: "resolved" as const } : alert)))
        setFilteredAlerts(filteredAlerts.map((alert) => (alert.id === id ? { ...alert, status: "resolved" as const } : alert)))
        loadTrend()
      } else {
        console.error('Failed to resolve alert:', response.error)
      }
    } catch (error) {
      console.error('Error resolving alert:', error)
    }
  }

  const archiveAlert = async (id: number) => {
    try {
      const response = await apiService.archiveAlert(id.toString())
      if (response.success) {
        setAlerts(alerts.map((alert) => (alert.id === id ? { ...alert, status: "archived" as const } : alert)))
        setFilteredAlerts(filteredAlerts.map((alert) => (alert.id === id ? { ...alert, status: "archived" as const } : alert)))
      } else {
        console.error('Failed to archive alert:', response.error)
      }
    } catch (error) {
      console.error('Error archiving alert:', error)
    }
  }

  const reopenAlert = async (id: number) => {
    try {
      const response = await apiService.reopenAlert(id.toString())
      if (response.success) {
        setAlerts(alerts.map((alert) => (alert.id === id ? { ...alert, status: "active" as const } : alert)))
        setFilteredAlerts(filteredAlerts.map((alert) => (alert.id === id ? { ...alert, status: "active" as const } : alert)))
      } else {
        console.error('Failed to reopen alert:', response.error)
      }
    } catch (error) {
      console.error('Error reopening alert:', error)
    }
  }

  const activeStatusFilter = filters?.status ?? 'all'
  const isResolvedTodayActive = activeStatusFilter === 'resolved_today'

  const handleCardFilterClick = (status: string) => {
    onStatusFilterChange?.(activeStatusFilter === status ? 'all' : status)
  }

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card
          className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)",
            boxShadow: activeStatusFilter === 'active'
              ? "0 0 0 3px #ffffff, 0 8px 32px rgba(0, 0, 0, 0.3)"
              : "0 8px 32px rgba(0, 0, 0, 0.3)",
          }}
          onClick={() => handleCardFilterClick('active')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <XCircle className="h-6 w-6 transition-transform duration-300 hover:scale-110" style={{ color: "#ffffff" }} />
              <div
                className="text-base px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff" }}
              >
                CRITICAL
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-4xl font-bold" style={{ color: "#ffffff" }}>
                {summaryStats?.active ?? '—'}
              </div>
              <div className="text-lg font-medium" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Active Alerts
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #991b1b 0%, #ef4444 100%)",
            boxShadow: activeStatusFilter === 'acknowledged'
              ? "0 0 0 3px #ffffff, 0 8px 32px rgba(0, 0, 0, 0.3)"
              : "0 8px 32px rgba(0, 0, 0, 0.3)",
          }}
          onClick={() => handleCardFilterClick('acknowledged')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className="h-6 w-6 transition-transform duration-300 hover:scale-110" style={{ color: "#ffffff" }} />
              <div
                className="text-base px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff" }}
              >
                PENDING
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-4xl font-bold" style={{ color: "#ffffff" }}>
                {summaryStats?.acknowledged ?? '—'}
              </div>
              <div className="text-lg font-medium" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Acknowledged
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #b91c1c 0%, #f87171 100%)",
            boxShadow: isResolvedTodayActive
              ? "0 0 0 3px #ffffff, 0 8px 32px rgba(0, 0, 0, 0.3)"
              : "0 8px 32px rgba(0, 0, 0, 0.3)",
          }}
          onClick={() => handleCardFilterClick('resolved_today')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Activity className="h-6 w-6 transition-transform duration-300 hover:scale-110" style={{ color: "#ffffff" }} />
              <div
                className="text-base px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff" }}
              >
                RESOLVED
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-4xl font-bold" style={{ color: "#ffffff" }}>
                {summaryStats?.resolvedToday ?? '—'}
              </div>
              <div className="text-lg font-medium" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Resolved Today
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #dc2626 0%, #fca5a5 100%)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="h-6 w-6 transition-transform duration-300 hover:scale-110" style={{ color: "#ffffff" }} />
              <div
                className="text-base px-2 py-1 rounded-full font-medium"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff" }}
              >
                TREND
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-4xl font-bold" style={{ color: "#ffffff" }}>
                {weekTrend === null
                  ? '—'
                  : weekTrend === 0
                  ? '0%'
                  : `${weekTrend > 0 ? '+' : ''}${weekTrend}%`}
              </div>
              <div className="text-lg font-medium" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                vs Last Week
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Table */}
      <Card
        className="border-0"
        style={{
          background: "linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-medium flex items-center gap-2" style={{ color: "#e2e8f0" }}>
              <AlertTriangle className="h-5 w-5" style={{ color: "#ef4444" }} />
              Critical Alerts
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={loadAlerts}
              disabled={loading}
              className="border-0 hover:bg-[#1a1a1a] h-9 bg-transparent"
              style={{
                background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                color: "#94a3b8",
              }}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {error && (
            <div
              className="m-4 p-6 rounded-lg border-l-4"
              style={{
                background: "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)",
                borderLeftColor: "#ef4444"
              }}
            >
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1" style={{ color: "#ef4444" }}>
                    Failed to Load Alerts
                  </h3>
                  <p className="text-base" style={{ color: "#94a3b8" }}>
                    {error}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadAlerts}
                    className="mt-3 border-0"
                    style={{
                      background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                      color: "#94a3b8",
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!error && filteredAlerts.length === 0 && !loading && (
            <div className="p-12 text-center">
              <Activity className="h-12 w-12 mx-auto mb-4" style={{ color: "#64748b" }} />
              <p className="text-lg" style={{ color: "#94a3b8" }}>
                No alerts found
              </p>
            </div>
          )}

          {/* Pagination controls — top */}
          {!error && totalCount > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: '#64748b' }}>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="text-sm rounded px-2 py-1 border-0 outline-none cursor-pointer"
                  style={{ backgroundColor: '#1a1a2e', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {PAGE_SIZE_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm" style={{ color: '#64748b' }}>
                  {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1 rounded transition-colors disabled:opacity-30"
                    style={{ color: '#94a3b8' }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm px-2" style={{ color: '#94a3b8' }}>
                    {page} / {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-1 rounded transition-colors disabled:opacity-30"
                    style={{ color: '#94a3b8' }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {!error && filteredAlerts.length > 0 && (
            <div className="space-y-1">
              {filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                className="p-4 hover:bg-[#1a1a1a30] transition-all duration-200 border-l-4 relative overflow-hidden"
                style={{
                  borderLeftColor:
                    alert.status === "active" ? "#ef4444"
                      : alert.status === "acknowledged" ? "#eab308"
                      : alert.status === "archived" ? "#64748b"
                      : "#22c55e",
                  background:
                    alert.status === "active"
                      ? "linear-gradient(135deg, rgba(239, 68, 68, 0.03) 0%, rgba(220, 38, 38, 0.03) 100%)"
                      : alert.status === "acknowledged"
                        ? "linear-gradient(135deg, rgba(234, 179, 8, 0.03) 0%, rgba(202, 138, 4, 0.03) 100%)"
                        : alert.status === "archived"
                          ? "linear-gradient(135deg, rgba(100, 116, 139, 0.03) 0%, rgba(71, 85, 105, 0.03) 100%)"
                          : "linear-gradient(135deg, rgba(34, 197, 94, 0.03) 0%, rgba(22, 163, 74, 0.03) 100%)",
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">{getAlertIcon(alert.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg" style={{ color: "#e2e8f0" }}>
                          {alert.title}
                        </h3>
                        {getSeverityBadge(alert.severity)}
                        {getStatusBadge(alert.status)}
                      </div>
                      <p className="text-lg mb-3 leading-relaxed" style={{ color: "#94a3b8" }}>
                        {alert.description}
                      </p>
                      <div className="flex items-center gap-4 text-base" style={{ color: "#64748b" }}>
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full" style={{ backgroundColor: "#06b6d4" }} />
                          <span>Site: {alert.site_id}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full" style={{ backgroundColor: "#06b6d4" }} />
                          <span>Client: {alert.client_id || "Unknown"}</span>
                        </div>
                        {alert.service_name && (
                          <div className="flex items-center gap-1">
                            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: "#10b981" }} />
                            <span>Service: {alert.service_name}</span>
                          </div>
                        )}
                        {alert.uuid && (
                          <div className="flex items-center gap-1">
                            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: "#f59e0b" }} />
                            <span>UUID: {alert.uuid.substring(0, 8)}...</span>
                          </div>
                        )}
                        {alert.batch_index && (
                          <div className="flex items-center gap-1">
                            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: "#ec4899" }} />
                            <span>Batch: {alert.batch_index}</span>
                          </div>
                        )}
                        {alert.count && (
                          <div className="flex items-center gap-1">
                            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: "#ff8697" }} />
                            <span>Count: {alert.count}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{new Date(alert.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {alert.status === "active" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => acknowledgeAlert(alert.id)}
                          className="border-0 hover:bg-[#2a2a2a] text-base h-12 px-6"
                          style={{
                            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                            color: "#94a3b8",
                          }}
                        >
                          Acknowledge
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => archiveAlert(alert.id)}
                          className="border-0 hover:bg-[#2a2a2a] text-base h-12 px-6"
                          style={{
                            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                            color: "#64748b",
                          }}
                        >
                          Archive
                        </Button>
                      </>
                    )}
                    {alert.status === "acknowledged" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resolveAlert(alert.id)}
                          className="border-0 hover:bg-[#2a2a2a] text-base h-12 px-6"
                          style={{
                            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                            color: "#94a3b8",
                          }}
                        >
                          Resolve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => archiveAlert(alert.id)}
                          className="border-0 hover:bg-[#2a2a2a] text-base h-12 px-6"
                          style={{
                            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                            color: "#64748b",
                          }}
                        >
                          Archive
                        </Button>
                      </>
                    )}
                    {alert.status === "resolved" && (
                      <div
                        className="text-base px-4 py-2 rounded-full font-medium"
                        style={{ backgroundColor: "rgba(34, 197, 94, 0.1)", color: "#22c55e" }}
                      >
                        ✓ Resolved
                      </div>
                    )}
                    {alert.status === "archived" && (
                      <>
                        <div
                          className="text-base px-4 py-2 rounded-full font-medium"
                          style={{ backgroundColor: "rgba(148, 163, 184, 0.1)", color: "#94a3b8" }}
                        >
                          📦 Archived
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reopenAlert(alert.id)}
                          className="border-0 hover:bg-[#2a2a2a] text-base h-12 px-6"
                          style={{
                            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                            color: "#94a3b8",
                          }}
                        >
                          Reopen
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
})

CriticalAlerts.displayName = "CriticalAlerts" 